import QRCode from 'qrcode';
import type { QrStyle } from '../types';
import { readableModuleColor, darken } from './brand';

export interface RenderAssets {
  /** Color de acento del tema (hex). Usado por color 'accent'/'gradient'. */
  accent?: string | null;
  /** Imagen de logo ya cargada (o null si no aplica / falló la carga). */
  logo?: HTMLImageElement | null;
}

const QUIET_ZONE = 4; // módulos de margen (zona de silencio estándar)
const TARGET_PX = 1024; // resolución de export (nítida para imprimir)

/** Indica si (row,col) cae dentro de uno de los tres ojos (finder patterns) 7×7. */
function isFinderRegion(row: number, col: number, size: number): boolean {
  const inTL = row < 7 && col < 7;
  const inTR = row < 7 && col >= size - 7;
  const inBL = row >= size - 7 && col < 7;
  return inTL || inTR || inBL;
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Dibuja un ojo (finder) con esquinas redondeadas: marco + hueco + núcleo. */
function drawRoundedFinder(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  scale: number,
  fill: string | CanvasGradient,
) {
  const s = 7 * scale;
  // Marco exterior (7×7) lleno
  ctx.fillStyle = fill;
  roundedRect(ctx, ox, oy, s, s, scale * 2);
  ctx.fill();
  // Hueco blanco (5×5)
  ctx.fillStyle = '#ffffff';
  roundedRect(ctx, ox + scale, oy + scale, 5 * scale, 5 * scale, scale * 1.4);
  ctx.fill();
  // Núcleo (3×3)
  ctx.fillStyle = fill;
  roundedRect(ctx, ox + 2 * scale, oy + 2 * scale, 3 * scale, 3 * scale, scale * 0.9);
  ctx.fill();
}

/**
 * Renderiza el QR de `value` en `canvas` con el estilo dado. Se usa tanto para el
 * preview en vivo como para producir el PNG que se guarda/comparte/sube a R2.
 *
 * Siempre ECC 'H' (alta corrección de errores) para tolerar el logo central sin
 * romper el escaneo. El fondo es blanco (con zona de silencio) para máximo contraste.
 */
export function renderQr(
  canvas: HTMLCanvasElement,
  value: string,
  style: QrStyle,
  assets: RenderAssets = {},
): void {
  const qr = QRCode.create(value, { errorCorrectionLevel: 'H' });
  const size = qr.modules.size;
  const data = qr.modules.data;

  const total = size + QUIET_ZONE * 2;
  const scale = Math.max(2, Math.floor(TARGET_PX / total));
  const px = total * scale;

  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Fondo blanco (incluye zona de silencio)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, px, px);

  // Color de los módulos
  let fill: string | CanvasGradient;
  const base = readableModuleColor(assets.accent ?? null);
  if (style.color === 'accent') {
    fill = base;
  } else if (style.color === 'gradient') {
    const grad = ctx.createLinearGradient(0, 0, px, px);
    grad.addColorStop(0, base);
    grad.addColorStop(1, darken(base, 0.55));
    fill = grad;
  } else {
    fill = '#000000';
  }

  const off = QUIET_ZONE * scale;
  const dots = style.shape === 'dots';

  // Ojos: en estilo "puntos" se dibujan redondeados una sola vez por esquina.
  const finderOrigins = dots
    ? [
        [0, 0],
        [size - 7, 0],
        [0, size - 7],
      ]
    : [];
  for (const [fc, fr] of finderOrigins) {
    drawRoundedFinder(ctx, off + fc * scale, off + fr * scale, scale, fill);
  }

  // Módulos
  ctx.fillStyle = fill;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (!data[row * size + col]) continue;
      if (dots && isFinderRegion(row, col, size)) continue; // ya dibujado como ojo
      const x = off + col * scale;
      const y = off + row * scale;
      if (dots) {
        const r = scale * 0.45;
        ctx.beginPath();
        ctx.arc(x + scale / 2, y + scale / 2, r, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, scale, scale);
      }
    }
  }

  // Logo central con recuadro de respiro blanco
  if (style.logoSource !== 'none' && assets.logo) {
    const boxSide = px * 0.2; // recuadro blanco
    const boxX = (px - boxSide) / 2;
    const boxY = (px - boxSide) / 2;
    ctx.fillStyle = '#ffffff';
    roundedRect(ctx, boxX, boxY, boxSide, boxSide, boxSide * 0.18);
    ctx.fill();

    const pad = boxSide * 0.16;
    const inner = boxSide - pad * 2;
    const img = assets.logo;
    const ratio = img.width && img.height ? Math.min(inner / img.width, inner / img.height) : 1;
    const w = (img.width || inner) * ratio;
    const h = (img.height || inner) * ratio;
    try {
      ctx.drawImage(img, (px - w) / 2, (px - h) / 2, w, h);
    } catch {
      // Si el logo contamina el canvas o falla el dibujo, se deja sin logo: el QR
      // sigue siendo válido. Las acciones (guardar/copiar) lo necesitan limpio.
    }
  }
}

/** Convierte el canvas a un Blob PNG (para guardar, copiar, compartir y subir). */
export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob failed'));
    }, 'image/png');
  });
}

/**
 * Carga una imagen de logo respetando CORS (necesario para no contaminar el canvas
 * al exportar). Devuelve null si falla — el render simplemente omite el logo.
 */
export function loadLogo(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}
